import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import type { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { toPascalCase } from 'components/WorkspaceEditor/utils'
import {
    InputBuilder,
    InputBuilderProps,
    TextContentElementMutation,
} from './InputBuilder'
import { ObjectBuilder } from './ObjectBuilder'

/** Input parameters for the mutation to update the content item element. */
interface SetProjectContentItemElementsInput {
    projectProductFamilyId: string
    contentItemId: string
    contentItemOwnerType: string
    elements: TextContentElementMutation[]
    projectId: string
    contentItemOwnerId: string
    nodeId: string
    textType: string
    partType: string
    documentTemplateViewId: string
    documentTemplateViewItemId: string
    contentItemParentId: string
    contentItemParentType: string
    propertyKey?: string
    isFromStudioManagementModule: boolean
    specifierNotes?: string[]
    isMasterSpecCloneNode: boolean
    selectedPillElementId: string
    isDeletingWholeParagraph: boolean
    isDeletingBaseContent: boolean
}

/** Variables for the mutation to update the content item element. */
interface SetProjectContentItemElementsProps {
    input: SetProjectContentItemElementsInput
    partType: string
    projectId: string
    rootArticleContentItemIds: string[]
    productFamilyId: string
    isTrackChangeToggleOn: boolean
}

export interface ContentItemInputBuilderProps extends InputBuilderProps {
    changedElements: Partial<TextContentElement>[]
}

/**
 * A class that encapsulates helper functions for building input variables
 * for updating project content item elements.
 * @param {ContentItemInputBuilderProps} props - The properties for the builder.
 * @param {string} props.contentItemId - The ID of the content item.
 * @param {Partial<TextContentElement>[]} props.changedElements - The elements to be changed.
 * @param {string} props.selectedProjectElementNodeId - The ID of the selected project element node.
 * @param {string} props.elementId - The ID of the element.
 * @param {ContentItemViewNodesType[]} props.contentItemViewNodes - The view nodes of the content item.
 */
export class ContentItemInputBuilder extends InputBuilder<
    SetProjectContentItemElementsInput,
    SetProjectContentItemElementsProps,
    Partial<TextContentElement>[]
> {
    constructor({
        contentItemId,
        changedElements,
        selectedProjectElementNodeId,
        elementId,
        contentItemViewNodes,
        projectId = '',
    }: ContentItemInputBuilderProps) {
        super(
            {
                contentItemId,
                selectedProjectElementNodeId,
                elementId,
                contentItemViewNodes,
                projectId,
            },
            // pass changedElements as the specific data (TData)
            changedElements
        )
        /** get the current ContentItem value */
        this.contentItem = this.getContentItem(contentItemId)
        /** use projectId from url if not already set */
        if (this.contentItem && !this.contentItem.projectId)
            this.contentItem.projectId = projectId
    }

    /**
     * Retrieves the content item node from global store by its ID.
     * @param contentItemId - The ID of the content item.
     * @returns The view node containing the content item.
     */
    protected getContentItem = (contentItemId: string) =>
        this.contentItemViewNodes.find((node) =>
            node.elements?.some((element) => element.id === contentItemId)
        ) as ContentItemViewNodesType

    /**
     * Retrieves the root article node from content item view nodes (typically from global store).
     * Recursively searches upwards based on dtvParentId.
     * @param node - The node to start the search from.
     * @returns The root article node.
     */
    protected getRootArticle = (
        node: Partial<ContentItemViewNodesType> | undefined
    ): Partial<ContentItemViewNodesType> | undefined => {
        if (!node) {
            return undefined
        }
        if (node.textType === 'Article') {
            return node
        }
        return this.getRootArticle(
            this.contentItemViewNodes.find(
                (ci) => ci.dtvItemId === node.dtvParentId
            )
        )
    }

    /**
     * Builds the elements array for the mutation.
     * Replaces the target element within the content item's elements
     * with the changed elements and maps them to the mutation model.
     * @returns An array of TextContentElementMutation.
     */
    protected buildElements = (): TextContentElementMutation[] => {
        const updatedElements = this.replaceInArray(
            this.contentItem?.elements || [],
            this.specificData // (changedElements)
        )

        return updatedElements.map(this.toMutationModel.bind(this))
    }

    /**
     * Maps the content item and changed elements into a Builder instance
     * containing the input for the updateProjectContentItemElements mutation.
     *
     * @param builder - A Builder for SetProjectContentItemElementsInput.
     * @returns The builder with the input set.
     */
    protected buildInput = (
        builder: ObjectBuilder<SetProjectContentItemElementsInput>
    ): ObjectBuilder<SetProjectContentItemElementsInput> => {
        const elements = this.buildElements()
        return (
            builder
                .resolveWith(this.nullOrUndefinedResolver)
                .set('projectProductFamilyId', this.elementId)
                .set('contentItemId', this.contentItem?.id)
                .set(
                    'contentItemOwnerType',
                    toPascalCase(this.contentItem?.type)
                )
                .set('elements', elements)
                .set('projectId', this.contentItem?.projectId)
                .set(
                    'contentItemOwnerId',
                    this.contentItem?.propertyRefId ||
                        this.contentItem?.dtvItemId
                )
                .set('nodeId', this.selectedProjectElementNodeId)
                .set('textType', this.contentItem?.textType)
                .set(
                    'partType',
                    this.contentItem?.partType?.toLocaleUpperCase() || ''
                )
                .set(
                    'documentTemplateViewId',
                    this.contentItem?.documentTemplateViewId
                )
                .set('documentTemplateViewItemId', this.contentItem?.dtvItemId)
                .set('contentItemParentId', this.contentItem?.dtvParentId)
                .set(
                    'contentItemParentType',
                    this.getParentType(this.contentItem?.dtvParentId || '')
                )
                .set('isFromStudioManagementModule', false)
                .set('isMasterSpecCloneNode', false)
                // for PT content, use 1st element id as the selected pill element id
                // otherwise use the content item id
                .set(
                    'selectedPillElementId',
                    this.contentItem?.partType?.toLocaleUpperCase() ===
                        'PRODUCTS'
                        ? elements[0].id
                        : this.contentItemId
                )
                .set('isDeletingWholeParagraph', false)
                .set('isDeletingBaseContent', !this.contentItem?.isUserAdded)
        )
    }

    /**
     * Builds the input variables for the mutation.
     * @returns The variables to be used in mutation.
     */
    build = (): SetProjectContentItemElementsProps => {
        const rootArticle = this.getRootArticle(this.contentItem)

        return new ObjectBuilder<SetProjectContentItemElementsProps>()
            .resolveWith(this.nullOrUndefinedResolver)
            .set('input', this.buildInput.bind(this))
            .set('partType', this.contentItem?.partType?.toLocaleUpperCase())
            .set('projectId', this.contentItem?.projectId)
            .set('rootArticleContentItemIds', [rootArticle?.id || ''])
            .set('productFamilyId', this.elementId)
            .set('isTrackChangeToggleOn', true)
            .build()
    }
}
